/**
 * 오버레이·시트·상세 — 시스템 뒤로가기(◀)로 닫기 (채팅방과 동일 UX)
 *
 * 새 기능 추가 시:
 * - v-if로 마운트되는 시트 → useMountedBackLayer
 * - open/show ref로 토글 → useBackCloseable
 */
import {
  onMounted,
  onUnmounted,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from 'vue'
import {
  closeBackLayer,
  dropBackLayer,
  pushBackLayer,
} from '@/composables/useAppBackNavigation'

/** 레이어 id 모음 — 부모에서 closeBackLayer 할 때 동일 id 사용 */
export const BACK_LAYER = {
  scheduleVisit: 'schedule-visit-detail',
  scheduleChange: 'schedule-change-request',
  scheduleReview: 'schedule-review',
  scheduleVisitType: 'schedule-visit-type',
  scheduleVisitHistory: 'schedule-visit-history',
  scheduleMemo: 'schedule-memo',
  scheduleYm: 'schedule-ym-picker',
  scheduleDay: 'schedule-day-sheet',
  scheduleRecipient: 'schedule-recipient-sheet',
  wageDetail: 'center-wage-detail',
  wageSlipView: 'center-wage-slip-view',
  chatRequest: 'center-chat-request',
  chatRequestHistory: 'center-chat-request-history',
  chatAttach: 'center-chat-attach',
  chatFeatures: 'center-chat-features',
  chatWageBroadcast: 'center-wage-broadcast',
  chatPeerProfile: 'chat-peer-profile',
  centerDocSheet: 'center-doc-sheet',
  staffDocSurface: 'staff-doc-surface',
  staffChat: 'staff-chat',
  docResign: 'doc-resign',
  docCert: 'doc-cert',
  docPreview: 'doc-preview',
  docRequestHistory: 'doc-request-history',
  ownerApprove: 'owner-approve',
  ownerStaffRegister: 'owner-staff-register',
  ownerBenForm: 'owner-ben-form',
  ownerMemberDetail: 'owner-member-detail',
  ownerRfidPanel: 'owner-rfid-panel',
  ownerManageHelp: 'owner-manage-help',
  ownerAcctView: 'owner-acct-view',
  profileNotify: 'profile-notify',
  profileOwnerLink: 'profile-owner-link',
  profileOwnerLinkDetail: 'profile-owner-link-detail',
  profileHub: 'profile-hub',
  profileFacility: 'profile-facility',
  rfidReserveList: 'rfid-reserve-list',
  rfidReserveEdit: 'rfid-reserve-edit',
  swApprove: 'sw-approve',
  pwaInstall: 'pwa-install',
  terms: 'terms-sheet',
  centerChatRoom: 'center-chat-room',
  featureTour: 'feature-tour',
  caregiverTodayPlan: 'caregiver-today-plan',
  receiptDetail: 'owner-receipt-detail',
  receiptMeta: 'owner-receipt-meta',
  fnaOrgSwitch: 'fna-org-switch',
} as const

/**
 * v-if로만 존재하는 시트 (마운트 = 열림).
 * 부모 UI 닫기 시 closeBackLayer(동일 id) 호출 권장.
 */
export function useMountedBackLayer(id: string, onBack: () => void) {
  onMounted(() => {
    pushBackLayer(id, onBack)
  })
  onUnmounted(() => {
    dropBackLayer(id)
  })
}

/**
 * boolean/computed open 상태를 감시해 레이어를 쌓음.
 * UI에서 닫을 때는 open만 false로 바꾸면 history 동기화됨.
 *
 * ★ popstate → onBack → open=false 경로에서는 이미 history가 한 칸 빠진 상태라
 *   closeBackLayer(history.back)를 또 호출하면 앱이 종료될 수 있다.
 */
export function useBackCloseable(
  id: string,
  isOpen: MaybeRefOrGetter<boolean>,
  onBack: () => void
) {
  let closingFromPop = false

  watch(
    () => toValue(isOpen),
    (open, was) => {
      if (open && !was) {
        pushBackLayer(id, () => {
          closingFromPop = true
          try {
            onBack()
          } finally {
            // watch(flush pre/post)가 동기 구간에서 돌 수 있어 마이크로태스크로 해제
            queueMicrotask(() => {
              closingFromPop = false
            })
          }
        })
      } else if (!open && was) {
        if (closingFromPop) {
          dropBackLayer(id)
        } else {
          closeBackLayer(id)
        }
      }
    }
  )
  onUnmounted(() => {
    dropBackLayer(id)
  })

  /** open을 닫는 콜백만 실행 (watch가 history 처리) */
  const dismiss = () => {
    if (!toValue(isOpen)) return
    onBack()
  }
  return { dismiss }
}
